import { toLocalDateKey } from './dateUtils'

/** Legacy fixed sessionStorage key (older builds). Prefer per-import keys from `makeAscapImportSessionStorageKey`. */
export const ASCAP_WORKS_IMPORT_STORAGE_KEY = 'dizeeAscapWorksImportPayloads'

/** Unique key per upload so Music never reads stale/cleared storage (fixes Strict Mode / race “missing data”). */
export function makeAscapImportSessionStorageKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `dizeeAscapImport:${crypto.randomUUID()}`
  }
  return `dizeeAscapImport:${Date.now()}:${Math.random().toString(36).slice(2, 12)}`
}

export type AscapCatalogSongPayload = Record<string, unknown>

function defaultUnreleasedReleaseDate(): string {
  const d = new Date()
  d.setDate(d.getDate() + 14)
  return toLocalDateKey(d)
}

function normalizeHeader(h: string): string {
  return h.trim().replace(/:\s*$/, '').replace(/\s+/g, ' ').toLowerCase()
}

function parseCsvLine(line: string): string[] {
  const out: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < line.length; i += 1) {
    const c = line[i]
    if (c === '"') {
      if (inQuotes && line[i + 1] === '"') {
        cur += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
      }
      continue
    }
    if (c === ',' && !inQuotes) {
      out.push(cur.trim())
      cur = ''
      continue
    }
    cur += c
  }
  out.push(cur.trim())
  return out
}

function splitCsvPhysicalLines(text: string): string[] {
  const lines: string[] = []
  let cur = ''
  let inQuotes = false
  for (let i = 0; i < text.length; i += 1) {
    const c = text[i]
    if (c === '"') {
      if (inQuotes && text[i + 1] === '"') {
        cur += '"'
        i += 1
      } else {
        inQuotes = !inQuotes
        cur += c
      }
      continue
    }
    if ((c === '\n' || c === '\r') && !inQuotes) {
      if (c === '\r' && text[i + 1] === '\n') i += 1
      lines.push(cur)
      cur = ''
      continue
    }
    cur += c
  }
  if (cur.length > 0) lines.push(cur)
  return lines
}

function parsePercent(raw: string): number {
  const n = parseFloat(String(raw ?? '').replace(/%/g, '').replace(/,/g, '').trim())
  return Number.isFinite(n) ? n : 0
}

/**
 * ASCAP Works Catalog exposes **Own%** (writer / ownership share) and **Collect%** (collection /
 * administration share). Many publisher & administrator rows have **Own% = 0** and the real
 * figure only in **Collect%** — mapping Own% alone silently zeroes those splits.
 */
export function effectiveAscapSharePercent(_role: string, ownPct: number, collectPct: number): number {
  const own = Number.isFinite(ownPct) ? ownPct : 0
  const coll = Number.isFinite(collectPct) ? collectPct : 0
  if (own > 0) return own
  if (coll > 0) return coll
  return 0
}

function formatAscapShareNote(ownPct: number, collectPct: number): string {
  const parts: string[] = []
  if (ownPct > 0) parts.push(`Own ${ownPct}%`)
  if (collectPct > 0) parts.push(`Collect ${collectPct}%`)
  return parts.join(' · ')
}

/** Map ASCAP “Role” column to Music publishing contributor role labels. */
export function ascapInterestedPartyRoleToLabel(role: string): string {
  const r = String(role ?? '').trim().toUpperCase()
  if (!r) return 'Contributor'
  if (['CA', 'C', 'SA', 'A', 'AR', 'AP'].includes(r)) return 'Writer'
  if (r === 'E') return 'Publisher'
  if (r === 'AM') return 'Administrator'
  if (r === 'SE' || r === 'SR') return 'Subpublisher'
  return r
}

export function isAscapWorksCatalogHeaders(headers: string[]): boolean {
  const n = headers.map(normalizeHeader)
  const hasPartyCol =
    n.includes('interested parties') || n.includes('interested party') || n.includes('party name')
  return n.includes('ascap work id') && n.includes('work title') && hasPartyCol
}

type WorkGroup = {
  workId: string
  workTitle: string
  memberName: string
  iswc: string
  workLicensedByAscap?: string
  shareLicensedByAscap?: string
  /** When multiple ASCAP Work IDs map to the same ISWC, we merge — these are the non-primary IDs. */
  extraAscapWorkIds?: string[]
  rows: {
    party: string
    ipi: string
    role: string
    society: string
    ownPct: number
    collectPct: number
    registrationStatus: string
    partyStatus: string
  }[]
}

const WRITER_ROLE_CODES_MERGE = new Set(['CA', 'C', 'SA', 'A', 'AR', 'AP'])

/** Normalize ISWC cell to `T` + 10 digits, or empty when missing/invalid. */
function normalizeIswcCell(raw: string): string {
  const s = String(raw ?? '')
    .toUpperCase()
    .replace(/[^T0-9]/g, '')
  return /^T\d{10}$/.test(s) ? s : ''
}

function normalizeWorkTitleKey(title: string): string {
  return String(title || '')
    .toLowerCase()
    .replace(/[''`]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function writerIpisFromWorkGroup(g: WorkGroup): Set<string> {
  const s = new Set<string>()
  for (const row of g.rows) {
    if (!WRITER_ROLE_CODES_MERGE.has(row.role.trim().toUpperCase())) continue
    const d = String(row.ipi || '')
      .replace(/\D/g, '')
      .replace(/^0+/, '')
    if (d) s.add(d)
  }
  return s
}

function writerIpisOverlap(a: Set<string>, b: Set<string>): boolean {
  if (a.size === 0 || b.size === 0) return false
  for (const x of a) {
    if (b.has(x)) return true
  }
  return false
}

function interestedPartyRowKey(row: WorkGroup['rows'][number]): string {
  return [
    row.party.trim().toLowerCase(),
    row.ipi.trim(),
    row.role.trim().toUpperCase(),
    row.society.trim(),
    String(row.ownPct),
    String(row.collectPct),
  ].join('|')
}

/**
 * ASCAP often lists the same composition under multiple Work IDs (re-registrations) while sharing one ISWC.
 * Merge those clusters into a single work group; dedupe interested-party rows.
 */
function mergeWorkGroupCluster(parts: WorkGroup[]): WorkGroup {
  if (parts.length === 1) return parts[0]

  const sorted = [...parts].sort((a, b) => {
    const aAcc = a.rows.some((r) => /accepted/i.test(r.registrationStatus))
    const bAcc = b.rows.some((r) => /accepted/i.test(r.registrationStatus))
    if (aAcc !== bAcc) return aAcc ? -1 : 1
    return a.workId.localeCompare(b.workId, undefined, { numeric: true })
  })

  const primary = sorted[0]
  const workTitle = sorted.reduce(
    (best, g) => (g.workTitle.length > best.length ? g.workTitle : best),
    primary.workTitle,
  )

  const seen = new Set<string>()
  const rows: WorkGroup['rows'] = []
  for (const g of sorted) {
    for (const row of g.rows) {
      const k = interestedPartyRowKey(row)
      if (seen.has(k)) continue
      seen.add(k)
      rows.push(row)
    }
  }

  let iswc = ''
  for (const g of sorted) {
    const n = normalizeIswcCell(g.iswc)
    if (n) {
      iswc = n
      break
    }
  }
  if (!iswc) {
    const raw = sorted.map((g) => g.iswc.trim()).find(Boolean)
    if (raw) iswc = raw
  }

  const memberName = sorted.map((g) => g.memberName).find(Boolean) || ''

  const allIds = new Set<string>()
  for (const g of sorted) {
    allIds.add(g.workId)
    for (const x of g.extraAscapWorkIds ?? []) allIds.add(x)
  }
  const primaryId = primary.workId
  const extraAscapWorkIds = [...allIds]
    .filter((id) => id !== primaryId)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))

  return {
    workId: primaryId,
    workTitle,
    memberName,
    iswc,
    workLicensedByAscap: sorted.map((g) => g.workLicensedByAscap).find(Boolean),
    shareLicensedByAscap: sorted.map((g) => g.shareLicensedByAscap).find(Boolean),
    extraAscapWorkIds: extraAscapWorkIds.length > 0 ? extraAscapWorkIds : undefined,
    rows,
  }
}

/**
 * One payload per musical work: cluster by ISWC, then attach stray Work-ID-only rows when title + writers match.
 */
function consolidateAscapWorkGroups(groups: WorkGroup[]): WorkGroup[] {
  const byIswc = new Map<string, WorkGroup[]>()
  const noIswc: WorkGroup[] = []

  for (const g of groups) {
    const k = normalizeIswcCell(g.iswc)
    if (k) {
      if (!byIswc.has(k)) byIswc.set(k, [])
      byIswc.get(k)!.push(g)
    } else {
      noIswc.push(g)
    }
  }

  const out: WorkGroup[] = []
  for (const cluster of byIswc.values()) {
    out.push(cluster.length === 1 ? cluster[0] : mergeWorkGroupCluster(cluster))
  }

  for (const orphan of noIswc) {
    const tKey = normalizeWorkTitleKey(orphan.workTitle)
    const orphanWriters = writerIpisFromWorkGroup(orphan)
    const candidates = out.filter(
      (g) =>
        normalizeWorkTitleKey(g.workTitle) === tKey && writerIpisOverlap(orphanWriters, writerIpisFromWorkGroup(g)),
    )
    if (candidates.length === 1) {
      const idx = out.indexOf(candidates[0])
      out[idx] = mergeWorkGroupCluster([candidates[0], orphan])
    } else {
      out.push(orphan)
    }
  }

  return out
}

/**
 * Parse ASCAP “Works Catalog” export (CSV). One saved catalog item per **composition**
 * (ASCAP may use multiple Work IDs per ISWC — those are merged with combined party rows),
 * with `publishing_contributors` built from Interested Parties rows.
 */
export function parseAscapWorksCatalogCsv(text: string): {
  payloads: AscapCatalogSongPayload[]
  errors: string[]
} {
  const errors: string[] = []
  const trimmed = text.replace(/^\uFEFF/, '')
  const physical = splitCsvPhysicalLines(trimmed).filter((l) => l.trim().length > 0)
  if (physical.length < 2) {
    errors.push('File is empty or has no data rows.')
    return { payloads: [], errors }
  }

  const headerCells = parseCsvLine(physical[0])
  if (!isAscapWorksCatalogHeaders(headerCells)) {
    errors.push('This CSV does not look like an ASCAP Works Catalog export (missing Work Title / ASCAP Work ID columns).')
    return { payloads: [], errors }
  }

  const h = headerCells.map(normalizeHeader)
  const idx = (want: string | string[]) => {
    const wants = Array.isArray(want) ? want : [want]
    for (let i = 0; i < h.length; i += 1) {
      if (wants.includes(h[i])) return i
    }
    return -1
  }

  const colMember = idx('member name')
  const colTitle = idx('work title')
  const colWorkId = idx('ascap work id')
  const colParty = Math.max(
    idx('interested parties'),
    idx('interested party'),
    idx('party name'),
  )
  const colIpi = Math.max(idx('ipi number'), idx('ipi #'), idx('ipi'))
  const colRole = idx('role')
  const colSociety = idx('society')
  const colOwn = Math.max(idx('own%'), idx('own %'), idx('own'))
  const colCollect = Math.max(idx('collect%'), idx('collect %'), idx('collect'))
  const colIswc = Math.max(idx('iswc number'), idx('iswc'))
  const colRegStatus = Math.max(idx('registration status'), idx('registrationstatus'))
  const colPartyStatus = Math.max(idx('interested party status'), idx('interested partystatus'))
  const colWorkLicensed = Math.max(idx('work licenced by ascap'), idx('work licensed by ascap'))
  const colShareLicensed = Math.max(idx('share licenced by ascap'), idx('share licensed by ascap'))

  if (colTitle < 0 || colWorkId < 0 || colParty < 0) {
    errors.push('Could not find required columns in the header row.')
    return { payloads: [], errors }
  }

  const byWork = new Map<string, WorkGroup>()

  for (let r = 1; r < physical.length; r += 1) {
    const cells = parseCsvLine(physical[r])
    if (cells.length < headerCells.length - 4 && cells.every((c) => !c)) continue

    const workId = String(cells[colWorkId] ?? '').trim()
    const workTitle = String(cells[colTitle] ?? '').trim()
    if (!workId || !workTitle) continue

    const memberName = colMember >= 0 ? String(cells[colMember] ?? '').trim() : ''
    const party = String(cells[colParty] ?? '').trim()
    if (!party) continue

    const ipi = colIpi >= 0 ? String(cells[colIpi] ?? '').trim() : ''
    const role = colRole >= 0 ? String(cells[colRole] ?? '').trim() : ''
    const society = colSociety >= 0 ? String(cells[colSociety] ?? '').trim() : ''
    const ownPct = colOwn >= 0 ? parsePercent(cells[colOwn] ?? '') : 0
    const collectPct = colCollect >= 0 ? parsePercent(cells[colCollect] ?? '') : 0
    const iswcCell = colIswc >= 0 ? String(cells[colIswc] ?? '').trim() : ''
    const registrationStatus =
      colRegStatus >= 0 ? String(cells[colRegStatus] ?? '').trim() : ''
    const partyStatus = colPartyStatus >= 0 ? String(cells[colPartyStatus] ?? '').trim() : ''
    const workLic = colWorkLicensed >= 0 ? String(cells[colWorkLicensed] ?? '').trim() : ''
    const shareLic = colShareLicensed >= 0 ? String(cells[colShareLicensed] ?? '').trim() : ''

    let g = byWork.get(workId)
    if (!g) {
      g = {
        workId,
        workTitle,
        memberName,
        iswc: '',
        workLicensedByAscap: workLic || undefined,
        shareLicensedByAscap: shareLic || undefined,
        rows: [],
      }
      byWork.set(workId, g)
    }
    if (memberName && !g.memberName) g.memberName = memberName
    if (iswcCell && !g.iswc) g.iswc = iswcCell
    if (workLic && !g.workLicensedByAscap) g.workLicensedByAscap = workLic
    if (shareLic && !g.shareLicensedByAscap) g.shareLicensedByAscap = shareLic
    g.rows.push({
      party,
      ipi,
      role,
      society,
      ownPct,
      collectPct,
      registrationStatus,
      partyStatus,
    })
  }

  const rd = defaultUnreleasedReleaseDate()
  const payloads: AscapCatalogSongPayload[] = []

  const writerRoleCodes = new Set(['CA', 'C', 'SA', 'A', 'AR', 'AP'])

  const consolidatedGroups = consolidateAscapWorkGroups([...byWork.values()])

  for (const g of consolidatedGroups) {
    const primary =
      g.memberName ||
      g.rows.find((x) => ascapInterestedPartyRoleToLabel(x.role) === 'Writer')?.party ||
      ''

    const writerParties = [
      ...new Set(
        g.rows
          .filter((x) => writerRoleCodes.has(x.role.trim().toUpperCase()))
          .map((x) => x.party.trim())
          .filter(Boolean),
      ),
    ]
    const songwriter = writerParties.length > 0 ? writerParties.join(', ') : ''

    const publishing_contributors = g.rows.map((row) => {
      const roleLabel = ascapInterestedPartyRoleToLabel(row.role)
      const pct = effectiveAscapSharePercent(row.role, row.ownPct, row.collectPct)
      const shareNote = formatAscapShareNote(row.ownPct, row.collectPct)
      const metaBits: string[] = []
      if (shareNote) metaBits.push(shareNote)
      if (row.registrationStatus) metaBits.push(`Reg: ${row.registrationStatus}`)
      if (row.partyStatus) metaBits.push(row.partyStatus)
      return {
        name: row.party,
        role: roleLabel,
        percentage: pct,
        pro: row.society || '',
        ipi_number: row.ipi || '',
        publishing_line: metaBits.join(' · '),
      }
    })

    const notesParts = [`Imported from ASCAP Works Catalog.`]
    if (g.iswc) notesParts.push(`ISWC: ${g.iswc}.`)
    notesParts.push(`ASCAP Work ID: ${g.workId}.`)
    if (g.extraAscapWorkIds?.length) {
      notesParts.push(`Additional ASCAP Work ID(s) (same work / ISWC in file): ${g.extraAscapWorkIds.join(', ')}.`)
    }
    if (g.workLicensedByAscap) notesParts.push(`Work licensed by ASCAP: ${g.workLicensedByAscap}.`)
    if (g.shareLicensedByAscap) notesParts.push(`Share licensed by ASCAP: ${g.shareLicensedByAscap}.`)

    const ascapAllWorkIds = [
      g.workId,
      ...(g.extraAscapWorkIds ?? []),
    ].filter((id, i, a) => id && a.indexOf(id) === i)

    payloads.push({
      song_title: g.workTitle,
      primary_artist: primary,
      artist_name: primary,
      primary_artists: primary ? [primary] : [],
      featured_artists: '',
      songwriter: songwriter || undefined,
      status: 'unreleased',
      release_date: rd,
      registered_to_pro: true,
      publishing_contributors,
      internal_notes: notesParts.join(' '),
      source_catalog_id: g.workId,
      ascap_all_work_ids: ascapAllWorkIds,
      iswc: normalizeIswcCell(g.iswc) || undefined,
      isrc: '',
      visibility: 'team',
    })
  }

  if (payloads.length === 0) {
    errors.push('No works found in file (check that data rows include Work Title and ASCAP Work ID).')
  }

  return { payloads, errors }
}
